/**
 * Splits an array into chunks of a specified size.
 *
 * @template T - The type of elements in the array.
 * @param arr - The array to be chunked.
 * @param size - The size of each chunk.
 * @returns An array of arrays, where each sub-array is a chunk of the original array.
 */
export const chunkArray = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};
/**
 * Retrieves the input name based on the provided tag and optional subTag.
 *
 * @param tag - The main tag used to identify the input.
 * @param subTag - An optional sub-tag to further specify the input.
 * @returns A string representing the input name.
 */
const getInputName = (tag: string, subTag?: string) => {
  const parsedTag = subTag ? `${tag}_${subTag}` : tag;
  switch (parsedTag) {
    case "med_pressure_dia":
      return "Blood Pressure DIA";
    case "med_pressure_sys":
      return "Blood Pressure SYS";
    case "med_temperature":
      return "Temperature";
    case "med_pulse":
      return "Pulse Rate";
    case "med_breaths":
      return "Respiration Rate";
    case "med_sp02":
      return "SPO2";
  }
};

/**
 * Generates an action description based on the provided parameters.
 *
 * @param params - An object containing the parameters for generating the action description.
 * @param params.tag - The main tag used to identify the input.
 * @param params.value - The new value of the input.
 * @param params.subTag - An optional sub-tag to further specify the input.
 * @param params.suffix - An optional suffix to append to the value.
 * @param params.defaultValue - The previous value of the input, if any.
 * @returns A string describing the action performed.
 */
export const getAction = ({
  tag,
  subTag,
  suffix,
  value,
  defaultValue,
}: {
  tag: string;
  value: string;
  subTag?: string;
  suffix?: string;
  defaultValue: string | undefined;
}) => {
  const unit = suffix ?? "";
  if (defaultValue) {
    return `Changed ${getInputName(tag, subTag)} from ${defaultValue} ${unit} to ${value} ${unit}`;
  }
  return `Added ${getInputName(tag, subTag)} ${value} ${unit}`;
};
